import overlay_img from 'images/loginb2.webp'
import {useFormik} from 'formik'
import React, {useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useSearchParams} from 'react-router-dom'
import {AuthSignHeader} from 'i18n/AuthSignHeader'
import {LocalLink} from 'i18n/LocalLink'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import 'assets/styles.scss'
import {getOrganizationSignupSchema} from 'utils/validation-schema'
import {useI18n} from 'i18n/I18nContext'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {routes} from 'routes/routes'
import {RegisterData} from 'organization/Auth/Interfaces/register'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import {withLocalePath} from 'i18n/localePaths'
import GoogleIcon from 'assets/icons/svg/GoogleIcon'
import {OrgPromoOfferBanner} from 'organization/Auth/OrgPromoOfferBanner'
import {normalizePromoInviteToken} from 'organization/promoInviteToken'

const ErrorMsg = ({errors}: any) => {
  return (
    <>
      <p className="error-msg" style={{color: 'red'}}>
        {errors}
      </p>
    </>
  )
}

export function Register() {
  const {register, googleAuth} = useOrganizationAuth()
  const navigate = useLocalizedNavigate()
  const {t, locale} = useI18n()
  const [searchParams] = useSearchParams()
  const inviteFromUrl = normalizePromoInviteToken(searchParams.get('invite'))
  const orgRegisterPath = withLocalePath(locale, routes.organization.register)
  const orgRegisterUrl = `${SITE_ORIGIN}${orgRegisterPath}`
  const signupSchema = useMemo(() => getOrganizationSignupSchema(t), [t])
  const [isBtnDisable, setBtnDisable] = useState(false)

  const {handleSubmit, handleBlur, handleChange, errors, touched, values} =
    useFormik({
      initialValues: {
        organization_name: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        accept_terms: false,
        promo_invite_token: inviteFromUrl,
      },
      validationSchema: signupSchema,
      onSubmit: async (values: RegisterData) => {
        setBtnDisable(true)
        const registerResult = await register(values)

        if (registerResult === true) {
          toast.success(t('auth.registerSuccess'))
          navigate(routes.organization.list)
        } else if (
          typeof registerResult === 'object' &&
          registerResult?.requiresVerification
        ) {
          navigate(
            `${routes.organization.verifyEmail}?email=${encodeURIComponent(
              registerResult.email,
            )}`,
          )
        }

        setBtnDisable(false)
      },
    })

  const promoInviteForGoogle =
    inviteFromUrl || normalizePromoInviteToken(values.promo_invite_token)

  const onGoogleRegister = () => {
    if (!values.accept_terms) {
      toast.warning(t('auth.mustAcceptTermsForGoogle'))
      return
    }
    void googleAuth(promoInviteForGoogle)
  }

  return (
    <>
      <Helmet>
        <title>{t('auth.orgRegisterTitle')}</title>
        <meta name="robots" content="noindex,follow" />
        <meta name="description" content={t('auth.orgRegisterMeta')} />
        <link rel="canonical" href={orgRegisterUrl} />
        <meta property="og:title" content={t('auth.orgRegisterOgTitle')} />
        <meta property="og:url" content={orgRegisterUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <main className={`body-area bd-theme-light`}>
        <section className="signin__area">
          <div className="sign__main-wrapper">
            <div className="sign__left">
              <AuthSignHeader
                logo={
                  <div className="sign__logo">
                    <LocalLink to={routes.index}>
                      <img
                        style={{width: '150px', height: 'auto'}}
                        className="logo-black"
                        src={process.env.PUBLIC_URL + '/logo-dark.svg'}
                        alt={t('logo.alt')}
                      />
                    </LocalLink>
                  </div>
                }
                navLinks={
                  <div className="sign__link custome_link">
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.login}
                    >
                      {t('common.login')}
                    </LocalLink>
                    <LocalLink
                      className="sign__link-active"
                      to={routes.organization.register}
                    >
                      {t('common.register')}
                    </LocalLink>
                  </div>
                }
              />
              <div className="sign__center-wrapper text-center mt-35">
                <div className="sign__title-wrapper mb-40">
                  <h3 className="sign__title">
                    {t('auth.orgSignupPageTitle')}
                  </h3>
                  <p>{t('auth.orgSignupSubtitle')}</p>
                  {inviteFromUrl ? (
                    <OrgPromoOfferBanner
                      inviteToken={inviteFromUrl}
                      variant="register"
                    />
                  ) : null}
                </div>
                <div className="sign__input-form text-center">
                  <form onSubmit={handleSubmit}>
                    <div className="sign__input">
                      <input
                        type="text"
                        name="organization_name"
                        value={values.organization_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.orgOrganizationNamePlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-user-2"></i>
                      </span>
                      {touched.organization_name && (
                        <ErrorMsg errors={errors.organization_name} />
                      )}
                    </div>
                    <div className="sign__input">
                      <input
                        type="text"
                        name="first_name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.orgYourFirstNamePlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-user-2"></i>
                      </span>
                      {touched.first_name && (
                        <ErrorMsg errors={errors.first_name} />
                      )}
                    </div>

                    <div className="sign__input">
                      <input
                        type="text"
                        name="last_name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.orgYourLastNamePlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-user-2"></i>
                      </span>
                      {touched.last_name && (
                        <ErrorMsg errors={errors.last_name} />
                      )}
                    </div>
                    <div className="sign__input">
                      <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.signupEmailPlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-email"></i>
                      </span>
                      {touched.email && <ErrorMsg errors={errors.email} />}
                    </div>
                    <div className="sign__input">
                      <input
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.signupPasswordPlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-password"></i>
                      </span>
                      {touched.password && (
                        <ErrorMsg errors={errors.password} />
                      )}
                    </div>
                    <div className="sign__input">
                      <input
                        type="password"
                        name="password_confirmation"
                        value={values.password_confirmation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('auth.signupPasswordConfirmPlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-password"></i>
                      </span>
                      {touched.password_confirmation && (
                        <ErrorMsg errors={errors.password_confirmation} />
                      )}
                    </div>
                    <div className="sign__action">
                      <div className="sign__check">
                        <input
                          className="e-check-input"
                          type="checkbox"
                          id="register-user"
                          name="accept_terms"
                          checked={values.accept_terms}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <label
                          style={{textAlign: 'left'}}
                          className="sign__check-text"
                          htmlFor="register-user"
                        >
                          {t('auth.acceptTermsLead')}{' '}
                          <LocalLink
                            to={routes.terms}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('footer.terms')}
                          </LocalLink>
                          .
                        </label>
                      </div>
                      {touched.accept_terms && (
                        <ErrorMsg errors={errors.accept_terms} />
                      )}
                    </div>
                    <div className="sing__button mb-20">
                      <button
                        type="submit"
                        disabled={isBtnDisable}
                        className={
                          isBtnDisable
                            ? 'disableBtnStyle w-100 mb-20'
                            : 'input__btn w-100 mb-20'
                        }
                      >
                        {t('common.register')}
                      </button>
                    </div>
                  </form>
                  <div
                    className="org-register-google-wrap"
                    style={{
                      marginTop: 8,
                      marginBottom: 8,
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    <p
                      style={{
                        margin: '14px 0 10px',
                        fontSize: 14,
                        color: 'var(--clr-text-10, #7a7a7a)',
                        fontWeight: 500,
                      }}
                    >
                      {t('auth.orDividerShort')}
                    </p>
                    <button
                      type="button"
                      className="gamil__sign-btn w-100"
                      onClick={onGoogleRegister}
                      disabled={isBtnDisable}
                    >
                      <span>
                        <GoogleIcon />
                      </span>{' '}
                      {t('auth.signUpWithGoogle')}
                    </button>
                  </div>
                  <div className="if__account mt-90">
                    <p>
                      {t('auth.hasAccount')} <br />
                      <LocalLink to={routes.organization.login}>
                        {t('common.login')}
                      </LocalLink>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="sign__right">
              <div
                className="sign__input-thumb include__bg w-img"
                style={{
                  backgroundImage: `url(${overlay_img})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                }}
              ></div>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
